import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Compass, HelpCircle, Lightbulb, PlayCircle, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TOURS, screenGuide } from "@/lib/guides";

/**
 * Guidance for everyone, made for people new to software (docs/GUIDANCE.md):
 * - the person's progress and choices (GET/PUT /me/guide): tasks done, Start here at sign-in, tips on empty screens;
 * - the Help panel: the steps and tips for the screen you are on (opened by the ? Help button in each page header);
 * - "Show me" walkthroughs: the real screen, dimmed, with one button lit up at a time and what to do with it.
 */
const GuideContext = createContext({
  ready: false, state: { done: [], start_on_login: true, tips: true }, update: () => {}, markDone: () => {},
  startTour: () => {}, openHelp: () => {}, touring: false,
});
export const useGuide = () => useContext(GuideContext);

/** A person who sells (not an admin or accounts login): their home is Start here (docs/GUIDANCE.md). */
export function isSalesPerson(user, canAccess) {
  return !!user && !["admin", "accountant"].includes(user.role) && canAccess(["leads", "quotes", "visitors"]);
}

export function GuideProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ done: [], start_on_login: true, tips: true });
  const [ready, setReady] = useState(false);
  const [help, setHelp] = useState(false);
  const [tour, setTour] = useState(null);         // { name, i }

  useEffect(() => {
    if (!user?.id) { setReady(false); return; }
    api.get("/me/guide", { skipCache: true }).then(({ data }) => setState(data)).catch(() => {}).finally(() => setReady(true));
  }, [user?.id]);

  const update = useCallback((patch) => {
    setState((s) => ({ ...s, ...patch }));
    api.put("/me/guide", patch).then(({ data }) => setState(data)).catch(() => {});
  }, []);
  const markDone = useCallback((id) => {
    setState((s) => {
      if (s.done.includes(id)) return s;
      const done = [...s.done, id];
      api.put("/me/guide", { done }).catch(() => {});
      return { ...s, done };
    });
  }, []);
  const startTour = useCallback((name) => { if (TOURS[name]) { setHelp(false); setTour({ name, i: 0 }); } }, []);

  const value = useMemo(() => ({
    ready, state, update, markDone, startTour, openHelp: () => setHelp(true), touring: !!tour,
  }), [ready, state, update, markDone, startTour, tour]);

  return (
    <GuideContext.Provider value={value}>
      {children}
      {help && <HelpPanel onClose={() => setHelp(false)} onShowMe={startTour} />}
      {tour && <Walkthrough tour={tour} setTour={setTour} onFinish={(name) => { markDone(name); setTour(null); }} />}
    </GuideContext.Provider>
  );
}

/** The ? Help button in every page header. */
export function HelpButton() {
  const { openHelp } = useGuide();
  return (
    <button type="button" onClick={openHelp} data-testid="help-button" aria-label="Help for this screen"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[var(--color-border,#e5e7eb)] bg-white text-[var(--color-primary,#2563eb)] hover:bg-[var(--color-primary-soft,#eef2ff)]">
      <HelpCircle size={15} /> <span className="hidden sm:inline">Help</span>
    </button>
  );
}

function HelpPanel({ onClose, onShowMe }) {
  const { pathname } = useLocation();
  const g = screenGuide(pathname);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[120] flex justify-end bg-black/30" onClick={onClose}>
      <aside className="w-full sm:w-[400px] h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}
        role="dialog" aria-label="Help" data-testid="help-panel">
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2 font-heading font-semibold text-lg"><HelpCircle size={20} className="text-[var(--color-primary,#2563eb)]" /> Help</div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-gray-100" aria-label="Close help"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5 text-[15px] leading-relaxed">
          {g ? (
            <>
              <div>
                <h2 className="font-heading font-bold text-xl">{g.title}</h2>
                {g.what && <p className="text-gray-600 mt-1">{g.what}</p>}
              </div>
              {g.tour && (
                <button type="button" onClick={() => onShowMe(g.tour)} data-testid="help-show-me"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--color-primary,#2563eb)] text-white font-semibold">
                  <PlayCircle size={18} /> Show me on this screen
                </button>
              )}
              {g.steps.length > 0 && (
                <section>
                  <h3 className="font-semibold mb-2">{g.taskTitle ? `How to: ${g.taskTitle.toLowerCase()}` : "How to use it"}</h3>
                  <ol className="space-y-2" data-testid="help-steps">
                    {g.steps.map((s, i) => (
                      <li key={s} className="flex gap-3">
                        <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--color-primary-soft,#eef2ff)] text-[var(--color-primary,#2563eb)] font-bold grid place-items-center text-sm">{i + 1}</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}
              {g.tips.length > 0 && (
                <section className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                  <h3 className="font-semibold mb-2 flex items-center gap-2"><Lightbulb size={16} className="text-amber-600" /> Good to know</h3>
                  <ul className="space-y-1.5 list-disc ml-5">{g.tips.map((t) => <li key={t}>{t}</li>)}</ul>
                </section>
              )}
            </>
          ) : (
            <div>
              <h2 className="font-heading font-bold text-xl">This screen</h2>
              <p className="text-gray-600 mt-1">There's no step-by-step guide for this screen yet. Your manager can show you how it is used.</p>
            </div>
          )}
          <Link to="/start" onClick={onClose} className="flex items-center gap-2 font-semibold text-[var(--color-primary,#2563eb)]" data-testid="help-all-guides">
            <Compass size={17} /> All guides: Start here
          </Link>
          <p className="text-sm text-gray-500">Stuck? Nothing you do here is lost: ask your manager, or press Help again on any screen.</p>
        </div>
      </aside>
    </div>
  );
}

const PAD = 6;
const WAIT_MS = 6000;

/** One step at a time: open the screen, find the button, dim the rest, explain it. */
function Walkthrough({ tour, setTour, onFinish }) {
  const steps = TOURS[tour.name];
  const step = steps[tour.i];
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const elRef = useRef(null);
  const last = tour.i === steps.length - 1;

  const go = useCallback((i) => {
    if (i < 0) return;
    if (i >= steps.length) { onFinish(tour.name); return; }
    setTour({ ...tour, i });
  }, [steps.length, tour, setTour, onFinish]);

  useEffect(() => {
    if (step.to && pathname !== step.to) navigate(step.to);
  }, [step, pathname, navigate]);

  // Find the step's button (it may appear a moment later: a form opening, a page loading), then follow it.
  useEffect(() => {
    setRect(null);
    setMissing(false);
    elRef.current = null;
    const started = Date.now();
    let found = null;
    const tick = () => {
      const el = document.querySelector(step.target);
      if (el && el.getBoundingClientRect().width > 0) {
        if (found !== el) { found = el; elRef.current = el; el.scrollIntoView({ block: "center", behavior: "smooth" }); }
        const r = el.getBoundingClientRect();
        setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + 2 * PAD, height: r.height + 2 * PAD });
        setMissing(false);
      } else if (!found && Date.now() - started > WAIT_MS) {
        setMissing(true);
      }
    };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [step]);

  // "Tap the highlighted button": go on when it is pressed.
  useEffect(() => {
    if (!step.waitClick || !rect || !elRef.current) return undefined;
    const el = elRef.current;
    const on = () => setTimeout(() => go(tour.i + 1), 350);
    el.addEventListener("click", on, { once: true });
    return () => el.removeEventListener("click", on);
  }, [step, rect, go, tour.i]);

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") setTour(null); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [setTour]);

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const cardW = Math.min(360, vw - 24);
  let cardStyle = { left: (vw - cardW) / 2, top: vh / 2 - 90, width: cardW };
  if (rect) {
    const below = rect.top + rect.height + 12;
    const top = below + 190 < vh ? below : Math.max(12, rect.top - 202);
    cardStyle = { width: cardW, top, left: Math.min(Math.max(12, rect.left), vw - cardW - 12) };
  }
  return (
    <div className="fixed inset-0 z-[130] pointer-events-none" data-testid="walkthrough">
      {rect ? (
        <div className="absolute rounded-xl ring-4 ring-amber-400 transition-all duration-200"
          style={{ ...rect, boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)" }} />
      ) : <div className="absolute inset-0 bg-slate-900/55" />}
      <div className="absolute bg-white rounded-2xl shadow-2xl p-4 pointer-events-auto" style={cardStyle} role="dialog" aria-live="polite"
        data-testid="walkthrough-card">
        <div className="text-xs font-semibold text-gray-500 mb-1">Step {tour.i + 1} of {steps.length}</div>
        <p className="text-[15px] leading-snug text-gray-900">{step.text}</p>
        {missing && (
          <p className="text-xs text-amber-700 mt-2" data-testid="walkthrough-missing">
            This button isn't on your screen right now (the form may be closed, or it isn't part of your access). You can carry on.
          </p>
        )}
        {step.waitClick && rect && !missing && <p className="text-xs text-gray-500 mt-2">Tap the lit-up button to continue.</p>}
        <div className="flex items-center gap-2 mt-3">
          <button type="button" className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setTour(null)} data-testid="walkthrough-close">Close</button>
          <div className="flex-1" />
          {tour.i > 0 && (
            <button type="button" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm" onClick={() => go(tour.i - 1)}>
              <ChevronLeft size={15} /> Back
            </button>
          )}
          {(!step.waitClick || missing || !rect) && (
            <button type="button" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[var(--color-primary,#2563eb)] text-white text-sm font-semibold"
              onClick={() => go(tour.i + 1)} data-testid="walkthrough-next">
              {last ? "Done" : <>Next <ChevronRight size={15} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A short "what to do first" line where a list is empty (switched off per person in Start here). */
export function EmptyTip({ children, tour }) {
  const { state, startTour } = useGuide();
  if (!state.tips) return null;
  return (
    <div className="mx-auto max-w-xl mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2 text-left"
      data-testid="empty-tip">
      <Lightbulb size={17} className="shrink-0 mt-0.5 text-amber-600" />
      <div className="flex-1">{children}</div>
      {tour && TOURS[tour] && (
        <button type="button" onClick={() => startTour(tour)} className="shrink-0 font-semibold text-[var(--color-primary,#2563eb)] whitespace-nowrap">
          Show me
        </button>
      )}
    </div>
  );
}
